interface StoreRecord<T = unknown> {
  id: string;
  data: T;
  created_at: string;
  updated_at: string;
}

function getStoreConfig(): {
  appId: string;
  base: string;
  token: string;
} {
  return {
    appId: (window as any).__SPROUT_APP_ID__ ?? "unknown",
    base: (window as any).__SPROUT_API_BASE__ ?? "",
    token: (window as any).__SPROUT_STORE_TOKEN__ ?? "",
  };
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const { appId, base, token } = getStoreConfig();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  const url = `${base}/api/store/${appId}${path}`;
  let lastError: unknown;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const res = await fetch(url, { headers, ...options });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any).error ?? `Store error: ${res.status}`);
      }
      if (res.status === 204) return undefined as T;
      return res.json();
    } catch (err) {
      if (!(err instanceof TypeError)) throw err;
      lastError = err;
      if (attempt < 2) {
        await new Promise((r) => setTimeout(r, 500 * 2 ** attempt));
      }
    }
  }
  throw lastError;
}

export const store = {
  list: <T = unknown>(collection: string): Promise<StoreRecord<T>[]> =>
    request<{ records: StoreRecord<T>[] }>(`/${collection}`).then((r) => r.records),

  get: <T = unknown>(collection: string, id: string): Promise<StoreRecord<T>> =>
    request<{ record: StoreRecord<T> }>(`/${collection}/${id}`).then((r) => r.record),

  create: <T = unknown>(collection: string, data: T): Promise<StoreRecord<T>> =>
    request<{ record: StoreRecord<T> }>(`/${collection}`, {
      method: "POST",
      body: JSON.stringify({ data }),
    }).then((r) => r.record),

  update: <T = unknown>(collection: string, id: string, data: T): Promise<StoreRecord<T>> =>
    request<{ record: StoreRecord<T> }>(`/${collection}/${id}`, {
      method: "PUT",
      body: JSON.stringify({ data }),
    }).then((r) => r.record),

  delete: (collection: string, id: string): Promise<void> =>
    request<void>(`/${collection}/${id}`, { method: "DELETE" }),
};
